/**
 * Thin fetch wrappers for the SPA's /api/* endpoints.
 */

const API_BASE = '';

/**
 * Pulls a useful error message out of a server JSON envelope. Prefers the
 * translated "friendly" message (server-side error-translate.ts) but falls
 * back to error / reason / raw body for older endpoints or non-JSON bodies.
 *
 * Phase 1.10 (2026-06-29): the extracted message gets a final humanization
 * pass via humanizeErrorMessage() so dev-speak (Better Auth's
 * `[body.email] Invalid email address`, raw JSON envelopes, Zod field
 * paths) never reaches the user. Strips field-path prefixes, parses
 * accidentally-double-encoded JSON-string bodies, maps common validator
 * patterns to plain English. Sits at the single fetch-wrapper layer so
 * every `/api/*` callsite benefits without local changes.
 */
export function extractError(
  text: string,
  path: string,
  method: string,
  status: number,
): { message: string; detail: string | null; incidentId: string | null; code: string | null } {
  if (text === '') {
    return {
      // Phase 1 Step 1c MUST — friendlier fallback for empty-body errors
      // (typically the worker timing out or Cloudflare returning a bare
      // status). Detail preserves the dev-flavored string for SRE.
      message: friendlyFallbackForStatus(status),
      detail: `${method} ${path} failed (${status})`,
      incidentId: null,
      code: null,
    };
  }
  try {
    const parsed = JSON.parse(text) as {
      error?: string;
      message?: string;
      detail?: string;
      reason?: string;
      incident_id?: string | null;
      correlation_id?: string | null;
    };
    const rawFriendly = parsed.message ?? parsed.error ?? parsed.reason ?? text.slice(0, 200);
    const detail = parsed.detail ?? null;
    // Push #78 round 11 (item A foundation) — incident_id propagated from
    // withErrorEnvelope so the Toast's Copy-for-support payload carries the
    // sys__operator_alert.id support can grep for. Null when the server
    // couldn't write the alert.
    const incidentId = parsed.incident_id ?? parsed.correlation_id ?? null;
    // Push #78 round 22 (Ean dogfood 2026-06-16 #3) — capture the server's
    // `error` code separately so call sites can branch on the wire-protocol
    // code (e.g. ChatApprovalBanner: `credits_depleted` vs `approval_required`)
    // without parsing the user-facing message text. The friendly message
    // stays in `message`; this is the machine-readable sibling.
    const code = parsed.error ?? null;
    return {
      message: humanizeErrorMessage(rawFriendly, status),
      detail,
      incidentId,
      code,
    };
  } catch {
    // Non-JSON body, usually Cloudflare's HTML error page when the Worker
    // throws past every guard. Strip HTML tags so the toast at least reads.
    const stripped = text
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return {
      // Phase 1 Step 1c MUST — friendlier fallback when the body is
      // HTML-stripped to nothing (typically Cloudflare's HTML 5xx page).
      // Detail preserves the dev-flavored string for SRE.
      message:
        humanizeErrorMessage(stripped.slice(0, 200), status) || friendlyFallbackForStatus(status),
      detail: stripped.slice(0, 200) === '' ? `${method} ${path} failed (${status})` : null,
      incidentId: null,
      code: null,
    };
  }
}

/**
 * Phase 1.10 (2026-06-29): universal dev-speak-to-English humanizer for
 * server error messages. Sits inside extractError so EVERY API surface
 * benefits without local changes.
 *
 * Handles three classes of dev-leak:
 *
 *  1. Double-encoded JSON envelopes. Some upstream proxies (Better Auth,
 *     in particular) return their full error body as a string inside our
 *     orchestrator's message field. So the SPA sees
 *     `{"code":"VALIDATION_ERROR","message":"[body.email] Invalid email address"}`
 *     as the message. We detect a leading `{` + JSON-shape and unwrap
 *     recursively (capped at 3 levels for safety).
 *
 *  2. Zod / Better Auth field-path prefixes. Format `[body.email] ...`
 *     or `[query.id] ...`. Strip the bracketed prefix entirely; the user
 *     doesn't care which body field path it came from, the input label
 *     already tells them.
 *
 *  3. Validator-canonical phrasing. Map common Zod / standard validator
 *     verbiage to natural language ("Invalid email address" →
 *     "Please enter a valid email address.", "String must contain at
 *     least N character(s)" → "Must be at least N characters.").
 *
 * Always preserves the original sentence if no transformation applies —
 * server-side translated friendlies (error-translate.ts) pass through
 * untouched.
 */
export function humanizeErrorMessage(raw: string, _status: number): string {
  if (typeof raw !== 'string') return String(raw);
  let msg = raw.trim();
  if (msg === '') return '';

  // 1. Unwrap double-encoded JSON bodies (up to 3 levels deep).
  for (let depth = 0; depth < 3; depth += 1) {
    if (msg.startsWith('{') && msg.endsWith('}')) {
      try {
        const inner = JSON.parse(msg) as {
          message?: string;
          error?: string;
          reason?: string;
        };
        const innerMsg = inner.message ?? inner.error ?? inner.reason;
        if (typeof innerMsg === 'string' && innerMsg.trim() !== '' && innerMsg !== msg) {
          msg = innerMsg.trim();
          continue;
        }
      } catch {
        // Not actually JSON-parseable; fall through.
      }
    }
    break;
  }

  // 2. Strip Zod / Better Auth `[body.field] ` or `[query.id] ` prefixes.
  //    Match a single bracketed token at the start; tolerate optional
  //    whitespace + dot-separated path inside.
  msg = msg.replace(/^\[[^\]]+\]\s*/, '');

  // 3. Friendly-up common validator phrasing. Each rule is a regex + a
  //    natural-language replacement. Add to this table as new patterns
  //    surface; the goal is one-line additions per dev-speak instance.
  const replacements: Array<{ pattern: RegExp; replacement: string }> = [
    // Email
    {
      pattern: /^Invalid email( address)?\.?$/i,
      replacement: 'Please enter a valid email address.',
    },
    { pattern: /^must be a valid email\.?$/i, replacement: 'Please enter a valid email address.' },
    // Required
    { pattern: /^Required\.?$/i, replacement: 'This field is required.' },
    { pattern: /^Required field\.?$/i, replacement: 'This field is required.' },
    // Password length (Zod default phrasing)
    {
      pattern: /^String must contain at least (\d+) character\(s\)\.?$/i,
      replacement: 'Must be at least $1 characters.',
    },
    {
      pattern: /^String must contain at most (\d+) character\(s\)\.?$/i,
      replacement: 'Must be at most $1 characters.',
    },
    // Number-shape
    { pattern: /^Expected number, received .+\.?$/i, replacement: 'Please enter a number.' },
    { pattern: /^Expected string, received .+\.?$/i, replacement: 'Please enter a value.' },
    // URL
    { pattern: /^Invalid url\.?$/i, replacement: 'Please enter a valid URL.' },
    // Better Auth canned phrases
    { pattern: /^Invalid credentials\.?$/i, replacement: 'Email or password is incorrect.' },
    { pattern: /^User not found\.?$/i, replacement: 'No account matches that email.' },
    {
      pattern: /^User already exists\.?$/i,
      replacement: 'An account with that email already exists. Try signing in instead.',
    },
    {
      pattern: /^Email already in use\.?$/i,
      replacement: 'An account with that email already exists. Try signing in instead.',
    },
    { pattern: /^Password too short\.?$/i, replacement: 'That password is too short.' },
    {
      pattern: /^Failed to fetch\.?$/i,
      replacement: "We couldn't reach Olytic. Check your connection and try again.",
    },
  ];
  for (const { pattern, replacement } of replacements) {
    if (pattern.test(msg)) {
      return msg.replace(pattern, replacement);
    }
  }

  // 4. Capitalize first letter + ensure terminal punctuation. If the
  //    sentence is already capitalized + punctuated, this is a no-op.
  if (msg.length > 0 && msg[0] !== msg[0]?.toUpperCase()) {
    msg = msg.charAt(0).toUpperCase() + msg.slice(1);
  }
  if (msg.length > 0 && !/[.!?]$/.test(msg)) {
    msg = `${msg}.`;
  }
  return msg;
}

/**
 * Lori-grade fallback messages keyed on HTTP status. Used when the server
 * gave NO usable error message (empty body or HTML 5xx page from Cloudflare).
 * Keeps user-facing copy plain English; the dev-flavored
 * `${method} ${path} failed (${status})` lives in `detail` for SRE triage.
 */
function friendlyFallbackForStatus(status: number): string {
  if (status === 401 || status === 403) {
    return "You don't have permission to do this, or your session expired.";
  }
  // Push 4-1ah (2026-07-10): 402 = wallet block. Chat surfaces this via
  // ChatApprovalBanner (with a Top-up CTA); non-chat surfaces fall through
  // to a toast — copy points the user at the top-up affordance so recovery
  // is one click away regardless of where the error surfaced.
  if (status === 402) {
    return "You're out of credits. Top up to keep going.";
  }
  if (status === 404) return "We couldn't find that. It may have moved or been removed.";
  if (status === 408 || status === 504) {
    return 'The request took too long. Try again in a moment.';
  }
  if (status === 429) {
    return "You're moving fast. Give it a few seconds and try again.";
  }
  if (status >= 500 && status < 600) {
    return 'Something went wrong on our end. Try again, or refresh if it keeps happening.';
  }
  if (status >= 400 && status < 500) {
    return "We couldn't process that request. Double-check what you entered and try again.";
  }
  return 'Something unexpected happened. Try again in a moment.';
}

/**
 * Phase 2 Finishing Pass R2 Item 4 — network failure translation.
 *
 * Wraps a fetch promise so a hard network failure (offline, DNS miss,
 * connection reset, request abort, TLS error) lands as an ApiError(0, ...)
 * with friendly Lori-grade copy instead of bubbling a bare TypeError to the
 * caller. status=0 lets call sites that branch on err.status treat it as
 * "no response from server" distinct from 4xx/5xx server errors.
 *
 * Without this, a user with intermittent connectivity sees raw "fetch failed"
 * or "Failed to fetch" in the toast — dev-speak that doesn't tell them what
 * to do.
 */
async function fetchOrThrow(path: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(`${API_BASE}${path}`, init);
  } catch (err) {
    // Network-level failure (offline, DNS, TLS, abort). Translate to a
    // friendly ApiError so the toast renders plain copy. Detail preserves
    // the original error text for SRE.
    const detail = err instanceof Error ? err.message : String(err);
    throw new ApiError(
      0,
      "We couldn't reach Olytic. Check your connection and try again.",
      detail,
      null,
      'network_failure',
    );
  }
}

/**
 * Sub-agent IIIII (2026-07-29) — Track 4 silent 401 retry.
 *
 * Any protected /api/* call that returns 401 triggers ONE silent probe
 * of /api/me. If the probe succeeds, the Push 5-post-smoke-45 cookie-
 * rotation path has extended the session on this request, so we retry
 * the original call ONCE. If the probe itself 401s, the session is
 * truly dead — fall through and let the caller's normal 401 handling
 * (ApiError → SessionExpiryListener) redirect to /sign-in.
 *
 * Guards:
 *   1. Never retry /api/me itself (would loop).
 *   2. Never retry /api/signin, /api/signup, /api/signout, /api/refresh
 *      probes (pre-auth endpoints; a 401 there means bad credentials,
 *      not stale session).
 *   3. Never retry a request we've already retried (each fetchWithRetry
 *      call is a single retry budget).
 *   4. Probe budget is 3s — same shape as SessionProvider's
 *      SESSION_FETCH_BUDGET_MS. Beyond that we treat as probe-failed
 *      and fall through.
 *
 * Idempotency reasoning: the /api/me probe is a GET — safe. The retry
 * of the original request is ONE attempt bounded to the failure case
 * where the server just told us "unauthorized"; the server hasn't
 * side-effected any state. If the retry happens to produce a doubled
 * mutation (extremely unlikely — server dedup + idempotency keys handle
 * this on write endpoints), that's a real bug elsewhere. Ship the
 * simpler version; add per-method gating if a real doubled-mutation
 * bug surfaces.
 */
const NO_RETRY_PATH_PREFIXES: readonly string[] = [
  '/api/me',
  '/api/signin',
  '/api/signup',
  '/api/signout',
  '/api/forgot-password',
  '/api/reset-password',
  '/api/verify-email',
  '/api/verify-email-otp',
  '/api/send-verification-email',
  '/api/accept-invite',
];
const SILENT_PROBE_BUDGET_MS = 3_000;

function shouldSkipSilentRetry(path: string): boolean {
  return NO_RETRY_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/**
 * Fire a lightweight /api/me probe with a 3s budget. Returns true when
 * the probe returns 200 (session still valid; the server's cookie-
 * rotation path may have just extended it), false otherwise. Never
 * throws — the failure paths all resolve to `false` so callers can
 * branch cleanly.
 *
 * Uses raw fetch (not fetchOrThrow) so a network failure resolves to
 * `false` instead of bubbling up as an ApiError, and does NOT construct
 * an ApiError on non-200 responses so the /api/me 401 doesn't dispatch
 * `olytic:session-expired` from the probe itself (only the caller's
 * eventual error should trigger that).
 */
async function silentSessionProbe(): Promise<boolean> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SILENT_PROBE_BUDGET_MS);
  try {
    const res = await fetch(`${API_BASE}/api/me`, {
      credentials: 'include',
      signal: controller.signal,
    });
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Sub-agent IIIII (2026-07-29) — Track 4 wrapper. Calls fetchOrThrow;
 * if the result is a 401 AND the path is eligible for silent retry,
 * fires the probe + retries once. Returns the (possibly-retried)
 * Response so the caller can inspect its own ok / status / body.
 *
 * The caller is responsible for constructing the ApiError from a
 * non-2xx response body — this wrapper never throws on HTTP error
 * (only on hard network failure via fetchOrThrow's TypeError branch).
 */
async function fetchWithSilentRetry(path: string, init: RequestInit): Promise<Response> {
  const first = await fetchOrThrow(path, init);
  if (first.status !== 401) return first;
  if (shouldSkipSilentRetry(path)) return first;
  // Silent probe. If it succeeds, cookie has been rotated on this
  // request — retry the original call once. If the probe fails, drop
  // through with the original 401 so the caller's normal error path
  // handles it.
  const probeOk = await silentSessionProbe();
  if (!probeOk) return first;
  return fetchOrThrow(path, init);
}

export async function get<T>(path: string): Promise<T> {
  // Sub-agent IIIII (2026-07-29) — Track 4: silent 401 retry via
  // fetchWithSilentRetry. Any 401 fires ONE /api/me probe + retries
  // the original call once. Transparent to callers.
  const res = await fetchWithSilentRetry(path, {
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const { message, detail, incidentId, code } = extractError(text, path, 'GET', res.status);
    throw new ApiError(res.status, message, detail, incidentId, code);
  }
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  /** Human-friendly message (server-translated when possible). */
  public readonly friendly: string;
  /** Raw technical error from the server, if available. Render small below message. */
  public readonly detail: string | null;
  /**
   * Push #78 round 11 — sys__operator_alert.id propagated from server's
   * withErrorEnvelope. Toast's Copy-for-support clipboard string includes
   * this so support can `SELECT * FROM sys__operator_alert WHERE id = '…'`
   * and find the row in seconds instead of grepping Cloud Run logs.
   * Null when the server couldn't write the alert row.
   */
  public readonly incidentId: string | null;
  /**
   * Push #78 round 22 — server's machine-readable error code (the `error`
   * field in the JSON envelope). Lets call sites branch on the protocol
   * code without parsing user-facing message text. Examples:
   *   `credits_depleted` → workspace credits empty, admin must top up
   *   `OAUTH_REQUIRED` → connector OAuth missing
   * Push 71 (Sub-agent JJJ audit): `approval_required` retired — approval
   *   workflow dropped in Push 70a alongside billing__approval_request.
   * Null when the server didn't send a code (older endpoints + non-JSON).
   */
  public readonly code: string | null;
  constructor(
    public status: number,
    message: string,
    detail: string | null = null,
    incidentId: string | null = null,
    code: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.friendly = message;
    this.detail = detail;
    this.incidentId = incidentId;
    this.code = code;

    // Phase 1 Step 1c MUST — session expiry UX. Centralized 401 catch
    // fires a global event whenever ANY ApiError lands with status=401,
    // regardless of which endpoint caused it. SessionExpiryListener
    // (mounted in App.tsx) catches the event and routes the user to
    // /sign-in?next=<currentPath>&email=<email> with a friendly toast.
    // The listener guards against firing on auth pages + when session
    // state isn't yet 'authenticated', so the initial /api/me 401 on
    // first load doesn't trigger a redundant toast.
    if (status === 401 && typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('olytic:session-expired'));
      } catch {
        // Defensive — never let dispatch failure break the error path.
        // (e.g. SSR contexts where window is absent at construction.)
      }
    }
  }
}

export async function post<T>(path: string, body: unknown): Promise<T> {
  // Sub-agent IIIII (2026-07-29) — Track 4: silent 401 retry. See get()
  // for the retry envelope; POST retries the whole body once after a
  // probe-verified session extension.
  const res = await fetchWithSilentRetry(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  let parsed: unknown = null;
  if (text !== '') {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }
  if (!res.ok) {
    const { message, detail, incidentId, code } = extractError(text, path, 'POST', res.status);
    throw new ApiError(res.status, message, detail, incidentId, code);
  }
  return parsed as T;
}

export async function put<T>(path: string, body: unknown): Promise<T> {
  // Sub-agent IIIII (2026-07-29) — Track 4: silent 401 retry. See get()
  // for the retry envelope; PUT retries the whole body once after a
  // probe-verified session extension.
  const res = await fetchWithSilentRetry(path, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  let parsed: unknown = null;
  if (text !== '') {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }
  if (!res.ok) {
    const { message, detail, incidentId, code } = extractError(text, path, 'PUT', res.status);
    throw new ApiError(res.status, message, detail, incidentId, code);
  }
  return parsed as T;
}

export async function unwrapJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  const parsed: unknown = text === '' ? null : (JSON.parse(text) as unknown);
  if (!res.ok) {
    const msg =
      parsed !== null && typeof parsed === 'object' && 'message' in parsed
        ? String((parsed as { message: unknown }).message)
        : `${res.url} failed (${res.status})`;
    throw new ApiError(res.status, msg);
  }
  return parsed as T;
}

export async function handlePatchResponse(res: Response): Promise<unknown> {
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text === '' ? null : JSON.parse(text);
  } catch {
    parsed = null;
  }
  if (!res.ok) {
    const msg =
      parsed && typeof parsed === 'object' && parsed !== null && 'message' in parsed
        ? String((parsed as { message: unknown }).message)
        : `PATCH failed (${res.status})`;
    throw new ApiError(res.status, msg);
  }
  return parsed;
}
